import type { JobLogV2Data } from 'src/utils/logs/types';
import type { TubesCleanedGraphDataElement, TubesCleanedGraphDataSegment } from './types';
import { TimeTable } from 'src/utils/logs/logHelpers';
import { cloneDeep } from 'lodash';
import { RangeSet } from '../../visualizations/utils';

/** A collection of utility functions for converting parsed log data into a shape that can be used for drawing the tubes cleaned graph. */
export class TubesCleanedGraphTranslator {
  /**
   * Maps the `tubeCount` property into the form needed by the tubes cleaned graph.
   * Will always be useful for jobs generated with <2.0 firmware, as the `tubeCount` property
   * always exists. With the introduction of the `tubeSheet`'s `tubeStatusCount`, this may no
   * longer be generally used going forward.
   */
  public static readonly parseTubeCount = (
    tubeCount: JobLogV2Data['tubeCount'],
    timeTable: TimeTable,
    timeRangeMin: number,
    timeRangeMax: number,
    controllerOffRanges: RangeSet,
  ): TubesCleanedGraphDataSegment => {
    if (!tubeCount) return [];
    const data = cloneDeep(tubeCount)
      .filter(tube => typeof tube.msElapsed === 'number' && !isNaN(tube.msElapsed))
      .map(tube => {
        let msElapsedAdjusted = tube.msElapsed;
        controllerOffRanges.getRangesBeforePointInTime(tube.msElapsed).forEach(range => {
          const rangeMs = range.end - range.start;
          msElapsedAdjusted -= rangeMs;
        });
        return { ...tube, msElapsedAdjusted: msElapsedAdjusted };
      })
      .filter(tube => tube.msElapsedAdjusted >= timeRangeMin && tube.msElapsedAdjusted <= timeRangeMax)
      .sort((a, b) => {
        // 1st Priority - msElapsed
        // 2nd Priority - cleaned
        // 3rd Priority - uncleaned
        if (a.msElapsed < b.msElapsed) return -1;
        if (a.msElapsed > b.msElapsed) return 1;
        if (a.cleaned < b.cleaned) return -1;
        if (a.cleaned > b.cleaned) return 1;
        if (a.partial < b.partial) return -1;
        if (a.partial > b.partial) return 1;
        return 0;
      });

    return data.map(tube => {
      const result: TubesCleanedGraphDataElement = {
        msElapsed: tube.msElapsedAdjusted,
        timestamp: timeTable.getInstant(tube.msElapsed),
        cleaned: tube.cleaned,
        uncleaned: tube.partial,
        total: tube.total,
      };

      return result;
    });
  };
}
